<script setup>
  import { ref, computed, onMounted } from "vue";
  import { useRouter } from "vue-router";
  import { storeToRefs } from "pinia";
  import { useMasterReportsStore } from "@stores/masterReports/masterReports";
  import { IndexPageLayout, IndexPageFilters } from "@/components/App/IndexPage";
  import { ConfirmDialog } from "@/components/episto-ui/confirm-dialog";
  import { useConfirmDelete } from "@/utils/composables/useConfirmDelete";
  import MasterReportsTable from "@/components/Reports/MasterReports/MasterReportsTable.vue";
  import { useRowNavigation } from "@/composables/useRowNavigation";
  import { useToastsStore } from "@/stores/toastsStore";

  const toast = useToastsStore();
  const router = useRouter();
  const { openRow } = useRowNavigation();
  const masterReportsStore = useMasterReportsStore();
  const { masterReports, sortedMasterReports, loading } = storeToRefs(masterReportsStore);

  const searchQuery = ref("");

  const {
    isOpen: showDeleteDialog,
    target: masterReportToDelete,
    requestDelete: handleDelete,
    confirm: confirmDelete,
  } = useConfirmDelete(async (masterReport) => {
    try {
      await masterReportsStore.deleteMasterReport(masterReport.token);
    } catch (error) {
      toast.error(error.message || "Failed to delete master report");
    }
  });

  const filteredMasterReports = computed(() => {
    if (!searchQuery.value.trim()) return sortedMasterReports.value;
    const query = searchQuery.value.toLowerCase();
    return sortedMasterReports.value.filter((masterReport) => masterReport.name.toLowerCase().includes(query));
  });

  onMounted(() => {
    masterReportsStore.fetchMasterReports();
  });

  function navigateToNew() {
    router.push({ name: "MasterReportNew" });
  }

  function navigateToMasterReport(masterReport, event) {
    openRow({ name: "MasterReportSetup", params: { themeToken: masterReport.token } }, event);
  }

  function handleDownload(masterReport) {
    window.location.href = `/api/master_reports/${masterReport.token}/download`;
  }

  async function handleDuplicate(masterReport) {
    try {
      const duplicated = await masterReportsStore.duplicateMasterReport(masterReport.token);
      navigateToMasterReport(duplicated);
    } catch (error) {
      console.error("Failed to duplicate master report:", error);
    }
  }
</script>

<template>
  <IndexPageLayout
    title="Master Reports"
    action-label="New master report"
    @action="navigateToNew">
    <template #filters>
      <IndexPageFilters
        v-model:search-model-value="searchQuery"
        search-placeholder="Search master reports" />
    </template>

    <MasterReportsTable
      :master-reports="filteredMasterReports"
      :loading="loading"
      @open="navigateToMasterReport"
      @download="handleDownload"
      @duplicate="handleDuplicate"
      @delete="handleDelete">
      <template #empty>
        {{ masterReports.length === 0 ? "Create your first master report." : "No master reports match your search" }}
      </template>
    </MasterReportsTable>

    <template #dialogs>
      <ConfirmDialog
        v-model:open="showDeleteDialog"
        title="Delete Master Report?"
        confirm-label="Delete"
        @confirm="confirmDelete">
        This will delete "{{ masterReportToDelete?.name }}" and its slide and chart templates. Master reports
        used by existing reports cannot be deleted — deactivate them instead.
      </ConfirmDialog>
    </template>
  </IndexPageLayout>
</template>
